import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Preloader, Text } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import VerifyBlock from '@/components/elements/VerifyBlock/VerifyBlock';
import { DashboardModalsKeys } from '@/modules/core/Dashboard/DashboardModals/dashboardModals.types';
import { useAuthStore } from '@/modules/user/stores';
import { useQueryParams } from '@/utils/hooks';

import SignIn from '../SignIn';

import useVerifyEmailForContact from './queries/useVerifyEmailForContact.query';

export const CODE_QUERY_KEY = 'confirm_code';
export const EMAIL_QUERY_KEY = 'email';
export const HASH_ID_QUERY_KEY = 'hash_id';

const ContactVerification = () => {
  const { inited, auth } = useAuthStore();
  const history = useHistory();
  const { verifyEmail, isLoading, isSuccess } = useVerifyEmailForContact();
  const query = useQueryParams();

  const code = useMemo(() => query.get(CODE_QUERY_KEY), [query]);
  const email = useMemo(() => query.get(EMAIL_QUERY_KEY), [query]);
  const hashId = useMemo(() => query.get(HASH_ID_QUERY_KEY), [query]);
  const isUserAuthorized = useMemo(() => auth && inited, [auth, inited]);

  const handleShowSuccessPopup = useCallback(() => {
    if (isUserAuthorized) {
      history.push(`${ROUTES.HOME}#${DashboardModalsKeys.EmailSuccessfullyVerified}`);
    }
  }, [history, isUserAuthorized]);

  const handleConfirmContactEmail = useCallback(async () => {
    if (!email || !code || !hashId) {
      return;
    }

    if (!isUserAuthorized) {
      // removes parameters from url
      history.push(ROUTES.CONTACT_VERIFICATION);
    }

    try {
      await verifyEmail({ code, email, hashId });
      handleShowSuccessPopup();
    } catch (error) {
      console.error(error);
      alert('Something went wrong.\nPossible your account already verified. Try to login or reset your password');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  useEffect(() => {
    handleConfirmContactEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isUserAuthorized) {
      history.push(ROUTES.HOME);
    }
  }, [history, isUserAuthorized]);

  const verificationStatus = useMemo(() => {
    if (isLoading) {
      return <Preloader testId="contact-email-verification_preloader" />;
    }

    if (isSuccess) {
      return (
        <Box data-testId="contact-email-successfully-verified_block" margin={{ bottom: 'spacing24' }}>
          <VerifyBlock>
            <Text color="textOnColor" style={{ lineHeight: '20px' }}>
              Your account has been verified!
            </Text>
            <Text color="textOnColor" style={{ lineHeight: '20px' }}>
              Please login using your credentials.
            </Text>
          </VerifyBlock>
        </Box>
      );
    }
  }, [isLoading, isSuccess]);

  if (isUserAuthorized) {
    return null;
  }

  return <SignIn header={verificationStatus} />;
};

export default ContactVerification;
